import { useRef, type ReactNode } from "react";
import { cn } from "../utils/cx.js";

export interface SegmentedOption<V extends string> {
  value: V;
  label?: ReactNode;
  icon?: ReactNode;
  /** Required when the option renders icon-only — it becomes the accessible name. */
  title?: string;
  disabled?: boolean;
}

export interface SegmentedProps<V extends string> {
  options: SegmentedOption<V>[];
  value: V;
  onChange: (value: V) => void;
  size?: "sm" | "md";
  /** Accessible name for the group (e.g. "View mode"). */
  label: string;
  className?: string;
}

const SIZE = {
  sm: "h-8 gap-0.5 p-0.5",
  md: "h-9 gap-0.5 p-1",
} as const;

const ITEM_SIZE = {
  sm: "h-7 min-w-7 px-2 text-xs",
  md: "h-7 min-w-8 px-2.5 text-sm",
} as const;

/**
 * Small inline switch between a few mutually exclusive options — the classic
 * list/grid toggle. Radio semantics (one tab stop, arrows move between options)
 * rather than a row of buttons, so a keyboard user does not have to tab past
 * every option to reach the content.
 */
export function Segmented<V extends string>({
  options,
  value,
  onChange,
  size = "sm",
  label,
  className,
}: SegmentedProps<V>) {
  const ref = useRef<HTMLDivElement | null>(null);

  const move = (dir: 1 | -1) => {
    const usable = options.filter((o) => !o.disabled);
    const at = usable.findIndex((o) => o.value === value);
    const next = usable[(at + dir + usable.length) % usable.length];
    if (!next) return;
    onChange(next.value);
    // Keep focus on the selected option so the roving tab stop follows the value.
    ref.current?.querySelector<HTMLButtonElement>(`[data-value="${next.value}"]`)?.focus();
  };

  return (
    <div
      ref={ref}
      role="radiogroup"
      aria-label={label}
      className={cn(
        "inline-flex items-center rounded-lg border border-input bg-muted",
        SIZE[size],
        className,
      )}
      onKeyDown={(e) => {
        if (e.key === "ArrowRight" || e.key === "ArrowDown") {
          e.preventDefault();
          move(1);
        } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
          e.preventDefault();
          move(-1);
        }
      }}
    >
      {options.map((o) => {
        const selected = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={selected}
            aria-label={o.label == null ? o.title : undefined}
            title={o.title}
            data-value={o.value}
            disabled={o.disabled}
            tabIndex={selected ? 0 : -1}
            onClick={() => onChange(o.value)}
            className={cn(
              "inline-flex items-center justify-center gap-1.5 rounded-md font-medium transition-colors outline-none",
              "focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background",
              "disabled:pointer-events-none disabled:opacity-50",
              ITEM_SIZE[size],
              selected ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground",
            )}
          >
            {o.icon}
            {o.label}
          </button>
        );
      })}
    </div>
  );
}
